import { msg } from '@lingui/macro';
import type { I18n, MessageDescriptor } from '@lingui/core';

/**
 * Billing-mode labels, keyed by the server's billing mode.
 *
 * Each entry MUST be a literal `msg({ id, message })` call: `lingui extract`
 * reads the source statically, so a plain object literal is silently skipped and
 * the id never reaches the catalogues.
 */
export const BILLING_MODE_MESSAGES: Record<string, MessageDescriptor> = {
  INCLUDED: msg({ id: 'settlingIn.billingPreview.mode.included', message: 'Included' }),
  FREE: msg({ id: 'settlingIn.billingPreview.mode.free', message: 'Free' }),
  FLAT_TOTAL: msg({ id: 'settlingIn.billingPreview.mode.flatTotal', message: 'Flat total' }),
  FLAT_WEEKLY: msg({ id: 'settlingIn.billingPreview.mode.flatWeekly', message: 'Flat weekly' }),
  PER_DAY: msg({ id: 'settlingIn.billingPreview.mode.perDay', message: 'Per day' }),
  PER_HOUR: msg({ id: 'settlingIn.billingPreview.mode.perHour', message: 'Per hour' }),
};

/**
 * Subsidy-handling labels, keyed by the server's `subsidy_billing_choice`.
 *
 * The ids are the ones already shipped by the billing settings form, so no new
 * catalogue entries are introduced — the `message` defaults must stay
 * byte-identical to the existing `en` msgstr or extraction would rewrite them.
 */
export const SUBSIDY_BILLING_CHOICE_MESSAGES: Record<string, MessageDescriptor> = {
  SUBSIDY_FROM_SETTLING_IN_START: msg({
    id: 'billingSettings.settlingIn.subsidyFromSettlingIn',
    message: 'Subsidy from Settling-In Start',
  }),
  SUBSIDY_FROM_CONTRACT_START: msg({
    id: 'billingSettings.settlingIn.subsidyFromContract',
    message: 'Subsidy from Contract Start',
  }),
};

/**
 * Localized label for a settling-in billing mode.
 *
 * Falls back to the RAW value for an unmapped enum so a mode the frontend has
 * not learned yet still renders something rather than vanishing.
 */
export function resolveBillingModeLabel(i18n: I18n, mode?: string | null): string | undefined {
  if (!mode) return undefined;
  const descriptor = BILLING_MODE_MESSAGES[mode];
  return descriptor ? i18n._(descriptor) : mode;
}

/** Localized label for a subsidy billing choice; same unmapped-enum fallback. */
export function resolveSubsidyBillingChoiceLabel(
  i18n: I18n,
  choice?: string | null,
): string | undefined {
  if (!choice) return undefined;
  const descriptor = SUBSIDY_BILLING_CHOICE_MESSAGES[choice];
  return descriptor ? i18n._(descriptor) : choice;
}
